/* Runs — every journey execution: closed 4-status set, filterable, with
 * a drill-down inspector (?run=<id>). */
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    Play,
    RefreshCw
} from 'lucide-react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import {
    RunStatusBadge
} from '../../shared/badges';
import RunInspector, {
    fmtStamp
} from './RunInspector';
import type {
    RunRecord
} from '../../api/types';

const STATUSES = ['all', 'running', 'waiting', 'completed', 'cancelled'];

export default function RunsPage() {
    const {
        tenancy
    } = useAppStudio();
    const [search, setSearch] = useSearchParams();
    const [runs, setRuns] = useState<RunRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const status = search.get('status') || 'all';
    const activeRun = search.get('run');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const filters = status === 'all' ? {} : {
                status
            };
            setRuns(await appEngineService.listRuns(tenancy, filters));
        } catch {
            setRuns([]);
        } finally {
            setLoading(false);
        }
    }, [tenancy, status]);

    useEffect(() => {
        if (!activeRun) load();
    }, [load, activeRun]);

    const setParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(search);
        if (value) next.set(key, value);
        else next.delete(key);
        setSearch(next);
    };

    return (
        <div className="aps-page">
            <div className="aps-page-narrow">
                {activeRun ? (
                    <RunInspector runId={activeRun} onBack={() => setParam('run', null)} />
                ) : (
                    <>
                        <div className="aps-page-head">
                            <div className="aps-page-head-text">
                                <h1 className="aps-h2">Runs</h1>
                                <p className="aps-page-sub">
                                    One record per journey execution — Redis is the truth,
                                    checkpoints at surface boundaries.
                                </p>
                            </div>
                            <button className="aps-icon-btn" title="Refresh" onClick={load}>
                                <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                            </button>
                        </div>
                        <div className="aps-chip-row" style={{
                            marginBottom: 16
                        }}>
                            {STATUSES.map((s) => (
                                <button key={s}
                                    className={'aps-filter-chip' + (status === s ? ' active' : '')}
                                    onClick={() => setParam('status', s === 'all' ? null : s)}>
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="aps-table-wrap">
                            <table className="aps-table">
                                <thead>
                                    <tr>
                                        <th>Run</th>
                                        <th>Journey</th>
                                        <th>Status</th>
                                        <th>Run by</th>
                                        <th>Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map((r, i) => (
                                        <tr key={r.id || i} onClick={() => r.id && setParam('run', r.id)}>
                                            <td className="aps-mono" style={{
                                                fontWeight: 600
                                            }}>
                                                {r.id || '—'}
                                            </td>
                                            <td className="aps-mono">{r.journey || '—'}</td>
                                            <td><RunStatusBadge status={r.status} /></td>
                                            <td className="aps-mono">{r.run_by || '—'}</td>
                                            <td style={{
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {fmtStamp(r.updated_at)}
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && runs.length === 0 && (
                                        <tr style={{
                                            cursor: 'default'
                                        }}>
                                            <td colSpan={5}>
                                                <div className="aps-empty" style={{
                                                    padding: '30px 10px'
                                                }}>
                                                    <span className="aps-empty-icon"><Play size={20} /></span>
                                                    <span>
                                                        No {status === 'all' ? '' : `${status} `}runs yet —
                                                        open the app and walk a journey.
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
